// FAQ Component Class
import StickySidebar from 'sticky-sidebar';

class FAQComponent {
  #container;

  #offset = 174;

  #accordions;

  #filterInput;

  #boundHandleFilter;

  #stickySidebar;

  constructor(container) {
    this.#container = container;
    this.#accordions = container.querySelectorAll('.accordion');
    this.#filterInput = container.querySelector('.faq-filter input');
    this.#boundHandleFilter = this.handleFilter.bind(this);
    this.#init();
  }

  updateStickyNav() {
    if (this.#stickySidebar) {
      this.#stickySidebar.updateSticky();
    }
  }

  #init() {
    this.setupStickyNavigation();
    this.#setupAccordionBehavior();
    this.handleURLHash();
    this.#setupFiltering();
    this.#setupMoreQuestionsButtons();
  }

  #setupAccordionBehavior = () => {
    this.#accordions.forEach((accordion) => {
      accordion.addEventListener('show.bs.collapse', this.handleAccordionShow);
    });
  };

  handleAccordionShow = (event) => {
    const button = event.target.previousElementSibling.querySelector('.accordion-button');
    if (button) {
      setTimeout(() => {
        this.scrollIntoPosition(button);

        this.updateStickyNav();
      }, 350);
    }
  };

  handleURLHash() {
    if (!window.location.hash) return;

    const elementId = window.location.hash.substring(1);
    const accordionItem = document.getElementById(elementId);
    
    if (!accordionItem?.closest('.accordion')) return;

    const collapseElement = accordionItem.querySelector('.accordion-collapse');
    const button = accordionItem.querySelector('.accordion-button');
    
    if (collapseElement) {
      this.closeOtherAccordions(accordionItem, collapseElement);
      this.openTargetAccordion(collapseElement, button);
    }
  }

  closeOtherAccordions(accordionItem, targetCollapse) {
    const parentAccordion = accordionItem.closest('.accordion');
    parentAccordion.querySelectorAll('.accordion-collapse.show').forEach((item) => {
      if (item !== targetCollapse) {
        item.classList.remove('show');
        const btn = item.parentElement.querySelector('.accordion-button');
        if (btn) btn.classList.add('collapsed');
      }
    });

    this.updateStickyNav();
  }

  openTargetAccordion(collapseElement, button) {
    collapseElement.classList.add('show');
    if (button) {
      button.classList.remove('collapsed');
      this.scrollIntoPosition(button);
      this.updateStickyNav();
    }
  }

  scrollIntoPosition(element) {
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.scrollY - this.#offset;
    
    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    });
  }

  setupStickyNavigation() {
    const stickyNav = this.#container.querySelector('.faq-sticky-nav');
    if (!stickyNav) return;

    if (window.innerWidth < 768) {
      return;
    }

    this.initSticky(stickyNav);
    FAQComponent.setupNavLinksTracking(stickyNav);
  }

  initSticky(stickyNav) {
    this.#stickySidebar = new StickySidebar(stickyNav, {
      containerSelector: '.faq-container',
      topSpacing: this.#offset,
      bottomSpacing: 20,
    });
  }

  static setupNavLinksTracking(stickyNav) {
    const navLinks = stickyNav.querySelectorAll('a');
    const options = {
      root: null,
      rootMargin: '-50% 0px',
      threshold: 0,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const targetId = entry.target.id;
          navLinks.forEach((link) => {
            link.classList.toggle('active', link.getAttribute('href') === `#${targetId}`);
          });
        }
      });
    }, options);

    navLinks.forEach((link) => {
      const targetId = link.getAttribute('href')?.substring(1);
      const target = document.getElementById(targetId);
      if (target) observer.observe(target);
    });
  }

  #setupFiltering() {
    if (!this.#filterInput) return;
    this.#filterInput.addEventListener('input', this.#boundHandleFilter);
  }

  static updateFaqGroupVisibility(group) {
    const visibleItems = group.querySelectorAll('.accordion-item:not([style*="display: none"])');
    group.classList.toggle('d-none', visibleItems.length === 0);
  }

  #updateNoResultsVisibility(noResultsElement) {
    const totalVisibleItems = this.#container.querySelectorAll('.accordion-item:not([style*="display: none"])').length;
    if (noResultsElement) {
      noResultsElement.classList.toggle('d-none', totalVisibleItems > 0);
    }
  }

  #resetFilterState(accordionItems, faqGroups, noResultsElement) {
    FAQComponent.showAllItems(accordionItems);
    faqGroups.forEach((group) => group.classList.remove('d-none'));
    if (noResultsElement) noResultsElement.classList.add('d-none');
    this.updateStickyNav();
  }

  #handleSearchResults(faqGroups, noResultsElement) {
    faqGroups.forEach((group) => FAQComponent.updateFaqGroupVisibility(group));
    this.#updateNoResultsVisibility(noResultsElement);
    this.updateStickyNav();
  }

  handleFilter = (event) => {
    const searchTerm = event.target.value.toLowerCase();
    const accordionItems = this.#container.querySelectorAll('.accordion-item');
    const noResultsElement = this.#container.querySelector('.faq-no-results');
    const faqGroups = this.#container.querySelectorAll('.faq-group');
    
    if (searchTerm.length < 3) {
      this.#resetFilterState(accordionItems, faqGroups, noResultsElement);
      return;
    }

    FAQComponent.filterItems(accordionItems, searchTerm);
    this.#handleSearchResults(faqGroups, noResultsElement);
  };

  static showAllItems(items) {
    items.forEach((item) => {
      // eslint-disable-next-line no-param-reassign
      item.style.display = '';
    });
  }

  static filterItems(items, searchTerm) {
    items.forEach((item) => {
      const question = item.querySelector('.accordion-button')?.textContent.toLowerCase();
      const answer = item.querySelector('.accordion-content')?.textContent.toLowerCase();
      const keywords = item.getAttribute('data-keywords')?.toLowerCase() || '';

      // eslint-disable-next-line no-param-reassign
      item.style.display = FAQComponent.matchesSearch(question, answer, keywords, searchTerm)
        ? ''
        : 'none';
    });
  }

  static matchesSearch(question, answer, keywords, searchTerm) {
    return question?.includes(searchTerm)
      || answer?.includes(searchTerm)
      || keywords.includes(searchTerm);
  }

  #setupMoreQuestionsButtons() {
    this.#accordions.forEach((accordion) => {
      const items = accordion.querySelectorAll('.accordion-item');
      if (items.length <= 10) return;

      this.showMoreQuestionsButton(accordion, items);
    });

    this.updateStickyNav();
  }

  showMoreQuestionsButton(accordion, items) {
    FAQComponent.hideExtraItems(items);
    
    const moreButton = accordion.parentElement.querySelector('.more-questions-btn');
    moreButton.classList.remove('d-none');

    const handleMoreClick = () => {
      FAQComponent.showAllItems(items);
      moreButton.removeEventListener('click', handleMoreClick);
      moreButton.remove();

      this.updateStickyNav();
    };

    moreButton.addEventListener('click', handleMoreClick);
  }

  static hideExtraItems(items) {
    items.forEach((item, index) => {
      if (index >= 10) {
        // eslint-disable-next-line no-param-reassign
        item.style.display = 'none';
      }
    });
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const faqContainers = document.querySelectorAll('.faq-container');

  faqContainers.forEach((container) => {
    // eslint-disable-next-line no-new
    new FAQComponent(container);
  });
});
